/** All PAs for one operational day. Click a PA to open their 7-day timeline. */
import type { PaShift } from '../../services/workingHours.api';
import { formatTimeBaghdad as fmt } from '../../utils/date';
import { tbl } from '../ui/table';
import { cn } from '../../utils/cn';

const hours = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)} h`);
const gap = (m: number) => (m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m / 60} h`);
function vsSchedule(min: number | null, late: string, early: string) {
  if (min == null || Math.abs(min) < 5) return null;
  return <span className={min > 0 ? 'text-orange-700' : 'text-gray-500'}>{`${gap(Math.abs(min))} ${min > 0 ? late : early}`}</span>;
}
const HEAD = ['PA', 'Scheduled', 'Start', 'End', 'Shift', 'Working', 'Idle', 'Stops', 'Stops / h', 'Notes'];
const NUM = new Set(['Shift', 'Working', 'Idle', 'Stops', 'Stops / h']);
const TH = 'sticky top-0 z-10 bg-white/85 backdrop-blur-md shadow-[inset_0_-1px_0_rgba(0,0,0,0.06)]';
const PILL = 'inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600';

export default function DayTable({ rows, selected, onSelect }: { rows: PaShift[]; selected: number | null; onSelect: (driverId: number) => void }) {
  if (!rows.length) return <p className={tbl.empty}>No pickup-agent work on this day.</p>;
  return (
    <div className="-mx-3 max-h-[min(65vh,720px)] overflow-auto">
      <table className={tbl.table}>
        <thead>
          <tr className={tbl.headRow}>
            {HEAD.map((h) => <th key={h} className={cn(tbl.th, TH, NUM.has(h) && tbl.num)}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.driverId} onClick={() => onSelect(r.driverId)}
              className={cn(tbl.row, 'cursor-pointer', selected === r.driverId && 'bg-blue-50/70')}>
              <td className={cn(tbl.td, selected === r.driverId && 'shadow-[inset_3px_0_0_#2563eb]')}>
                <button type="button" dir="auto"
                  className="rounded font-medium text-gray-900 hover:underline focus-visible:ring-2 focus-visible:ring-blue-500 motion-safe:active:scale-[0.97]"
                  onClick={(e) => { e.stopPropagation(); onSelect(r.driverId); }}>
                  {r.name ?? `#${r.driverId}`}
                </button>
                <span className={`ml-2 ${tbl.muted}`}>#{r.driverId}</span>
              </td>
              <td className={cn(tbl.td, 'text-gray-600')}>{r.scheduled ? `${r.scheduled.start}–${r.scheduled.end}` : '—'}</td>
              <td className={tbl.td}>
                <div>{r.start ? fmt(r.start) : '—'}</div>
                <div className="text-xs">{vsSchedule(r.lateStartMin, 'late', 'early')}</div>
              </td>
              <td className={tbl.td}>
                {r.status === 'on shift' ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-500" />On shift
                  </span>
                ) : (
                  <>
                    <div>{r.end ? fmt(r.end) : '—'}</div>
                    <div className="text-xs">{vsSchedule(r.earlyFinishMin, 'early', 'over')}</div>
                  </>
                )}
              </td>
              <td className={cn(tbl.td, tbl.num)}>{hours(r.shiftH)}</td>
              <td className={cn(tbl.td, tbl.num, 'font-medium')}>{hours(r.workingH)}</td>
              <td className={cn(tbl.td, tbl.num)}>{r.idleMin == null ? '—' : `${r.idleMin} min`}</td>
              <td className={cn(tbl.td, tbl.num)}>
                {r.stops} <span className="text-[12px] text-gray-500">{`(${r.pickups} pk · ${r.rtoDrops} RTO)`}</span>
              </td>
              <td className={cn(tbl.td, tbl.num)}>{r.stopsPerH == null ? '—' : r.stopsPerH.toFixed(1)}</td>
              <td className={tbl.td}>
                <div className="flex flex-wrap gap-1">
                  {r.notes.map((n) => <span key={n} className={PILL}>{n}</span>)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
